import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { CloudArrowUpIcon, DocumentIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { showToast } from '../features/ui/uiSlice';
import api from '../utils/axiosConfig';

interface DataUploadProps {
    taskId: string;
    onUploadSuccess?: () => void;
    onClose?: () => void;
}

const DataUpload: React.FC<DataUploadProps> = ({ taskId, onUploadSuccess, onClose }) => {
    const dispatch = useDispatch();
    const [file, setFile] = useState<File | null>(null);
    const [description, setDescription] = useState('');
    const [uploading, setUploading] = useState(false);
    const [dragActive, setDragActive] = useState(false);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFile = e.target.files?.[0];
        if (selectedFile) {
            // Validate file type
            const allowedTypes = [
                'text/csv',
                'application/vnd.ms-excel',
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                'application/json',
                'text/plain'
            ];

            const allowedExtensions = ['.csv', '.xlsx', '.xls', '.json', '.txt'];
            const fileExtension = selectedFile.name.toLowerCase().substring(selectedFile.name.lastIndexOf('.'));

            if (!allowedTypes.includes(selectedFile.type) && !allowedExtensions.includes(fileExtension)) {
                dispatch(showToast({
                    message: 'Please select a valid file (CSV, Excel, JSON, or TXT)',
                    type: 'error'
                }));
                return;
            }

            // Validate file size (10MB limit)
            if (selectedFile.size > 10 * 1024 * 1024) {
                dispatch(showToast({
                    message: 'File size must be less than 10MB',
                    type: 'error'
                }));
                return;
            }

            setFile(selectedFile);
        }
    };

    const handleDrag = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setDragActive(true);
        } else if (e.type === 'dragleave') {
            setDragActive(false);
        }
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);

        const droppedFile = e.dataTransfer.files[0];
        if (droppedFile) {
            const input = document.createElement('input');
            input.type = 'file';
            input.files = e.dataTransfer.files;
            handleFileChange({ target: input } as any);
        }
    };

    const handleUpload = async () => {
        if (!file) {
            dispatch(showToast({
                message: 'Please select a file to upload',
                type: 'error'
            }));
            return;
        }

        setUploading(true);
        try {
            // First upload the file
            const formData = new FormData();
            formData.append('data', file);

            const uploadResponse = await api.post('/upload/data', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            if (uploadResponse.data.success) {
                // Then associate the file with the task
                const taskResponse = await api.post(`/tasks/${taskId}/upload-company-data`, {
                    dataPath: uploadResponse.data.dataPath,
                    description: description || `Data file: ${file.name}`
                });

                if (taskResponse.data.success) {
                    dispatch(showToast({
                        message: 'Data uploaded successfully',
                        type: 'success'
                    }));

                    setFile(null);
                    setDescription('');

                    if (onUploadSuccess) {
                        onUploadSuccess();
                    }
                }
            }
        } catch (error: any) {
            console.error('Upload error:', error);
            dispatch(showToast({
                message: error.response?.data?.message || 'Failed to upload data',
                type: 'error'
            }));
        } finally {
            setUploading(false);
        }
    };

    const removeFile = () => {
        setFile(null);
    };

    const formatFileSize = (bytes: number) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
                <div className="p-6">
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                            Upload Reference Data
                        </h2>
                        {onClose && (
                            <button
                                onClick={onClose}
                                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                            >
                                <XMarkIcon className="h-6 w-6" />
                            </button>
                        )}
                    </div>

                    <div className="space-y-6">
                        {/* File Upload Area */}
                        <div
                            className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${dragActive
                                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                                : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
                                }`}
                            onDragEnter={handleDrag}
                            onDragLeave={handleDrag}
                            onDragOver={handleDrag}
                            onDrop={handleDrop}
                        >
                            <CloudArrowUpIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                            <p className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                                Drop your file here or click to browse
                            </p>
                            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                                Supports CSV, Excel, JSON, and TXT files (max 10MB)
                            </p>
                            <input
                                type="file"
                                onChange={handleFileChange}
                                accept=".csv,.xlsx,.xls,.json,.txt"
                                className="hidden"
                                id="file-upload"
                            />
                            <label
                                htmlFor="file-upload"
                                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 cursor-pointer"
                            >
                                Choose File
                            </label>
                        </div>

                        {/* Selected File */}
                        {file && (
                            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-3">
                                        <DocumentIcon className="h-8 w-8 text-blue-500" />
                                        <div>
                                            <p className="font-medium text-gray-900 dark:text-white">
                                                {file.name}
                                            </p>
                                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                                {formatFileSize(file.size)}
                                            </p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={removeFile}
                                        className="text-red-500 hover:text-red-700"
                                    >
                                        <XMarkIcon className="h-5 w-5" />
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Description */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Description (Optional)
                            </label>
                            <textarea
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                placeholder="Describe what this data contains..."
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                                rows={3}
                            />
                        </div>

                        {/* Upload Button */}
                        <div className="flex justify-end space-x-3">
                            {onClose && (
                                <button
                                    onClick={onClose}
                                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 hover:bg-gray-200 dark:hover:bg-gray-500 rounded-md"
                                >
                                    Cancel
                                </button>
                            )}
                            <button
                                onClick={handleUpload}
                                disabled={!file || uploading}
                                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed rounded-md"
                            >
                                {uploading ? 'Uploading...' : 'Upload Data'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DataUpload;
